import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { Match2PowerupButton } from './Match2PowerupButton';
import PopupMatch2Base, { PopupMatch2BaseInitOptions } from './PopupMatch2Base';
import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import Match2Mascots from './Match2Mascots';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { runEnergyCheckSequence } from '../match2.sequence';
import Match2PopupSubHeader from './Match2PopupSubHeader';
import Match2BuruStreakPanel from './Match2BuruStreakPanel';
import { isMatch2UnlockableUnlocked } from 'src/replicant/match2/match2.getters';
import StateObserver from 'src/StateObserver';
import {
  canPlayLeague,
  getCurrentLeaguePhase,
} from 'src/replicant/match2/leagues/leagues.getters';
import { matchTheme } from 'src/game/theme/match2/Match2Theme';

type CloseHandler = (result: {
  retry: boolean;
  powerups?: Match2PowerupType[];
}) => void;

interface PopupMatch2LevelFailedInitOptions extends PopupMatch2BaseInitOptions {
  level: number;
}

export default class PopupMatch2LevelFailed extends PopupMatch2Base {
  private backView!: View;
  private mascots!: Match2Mascots;
  private powerups: Match2PowerupButton[] = [];
  private streak!: Match2BuruStreakPanel;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close({ retry: false }),
      title: {
        type: 'banner',
        color: 'red',
      },
      cta: {
        localeText: () => i18n('minigame.match2.retry').toLocaleUpperCase(),
        positioning: 'inside',
        onClick: async () => {
          if (!(await runEnergyCheckSequence())) return;
          opts.close({
            retry: true,
            powerups: this.powerups
              .filter((p) => p.isSelected)
              .map((p) => p.getType()),
          });
        },
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PUZZLE_FAILED_POPUP,
      },
    });

    this.createBackView();

    new LangBitmapFontTextView({
      superview: this.panel,
      font: 'Body',
      color: '#CB2934',
      size: 38,
      x: this.panel.style.width * 0.5,
      y: 90,
      centerOnOrigin: true,
      centerAnchor: true,
      localeText: () => i18n('match2.ui.levelFailed'),
      verticalAlign: 'center',
      align: 'center',
    });

    new Match2PopupSubHeader({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height * 0.5 - 85,
      localeText: () => i18n('match2.ui.selectBoosters'),
    });

    const powerupTypes: Match2PowerupType[] = ['rocket', 'bomb', 'cube'];
    const width = this.panel.style.width * 0.45;
    const diff = this.panel.style.width - width;

    powerupTypes.forEach((type, index) => {
      const panel = new Match2PowerupButton();

      panel.setType(type);
      panel.updateOpts({
        x: width * 0.5 * index + diff * 0.5,
        y: this.panel.style.height - 220,
        centerOnOrigin: true,
      });

      this.panel.addSubview(panel);
      this.powerups.push(panel);
    });

    this.createBuruStreakPanel();
  }

  private createBackView() {
    this.backView = new View({
      superview: this.root,
      centerOnOrigin: true,
      centerAnchor: true,
      x: this.panelView.style.x,
      y: this.panelView.style.y - this.panelView.style.height * 0.5,
      clipEvents: false,
      zIndex: 1,
    });

    this.mascots = new Match2Mascots({
      superview: this.backView,
      config: [
        {
          image: matchTheme().getUIAsset('mascot_3.png'),
          width: 271,
          height: 212,
          x: -155,
          y: -90,
        },
        {
          image: matchTheme().getUIAsset('mascot_4.png'),
          width: 337,
          height: 225,
          x: 135,
          y: -100,
        },
      ],
    });
    this.backView.hide();
  }

  private createBuruStreakPanel() {
    this.streak = new Match2BuruStreakPanel({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height + 190,
    });
  }

  public async init({
    level,
    useClickOutside,
  }: PopupMatch2LevelFailedInitOptions) {
    const { user } = StateObserver.getState();
    if (canPlayLeague(user)) {
      this.setTitleText(() =>
        i18n('match2.leagues.phase', {
          phase: getCurrentLeaguePhase(user),
        }).toLocaleUpperCase(),
      );
    } else {
      this.setTitleText(() =>
        i18n('match2.ui.level', { level }).toLocaleUpperCase(),
      );
    }
    super.init({ useClickOutside });
    this.powerups.forEach((powerup) => {
      const type = powerup.getType();
      const user = StateObserver.getState().user;
      powerup.setLocked(!isMatch2UnlockableUnlocked(user, type));
      powerup.autoUpdate();
    });
  }

  public override async fadeIn() {
    await super.fadeIn();
    this.backView.show();
    this.mascots.show();
    this.streak?.show();
  }

  public override async fadeOut() {
    this.streak?.hide?.();
    this.mascots.hide();
    this.backView.hide();
    super.fadeOut();
    this.powerups.forEach((powerup) => {
      powerup.stopAutoUpdate();
    });
  }
}
